/**
 * ContactFormDialog Component
 * 
 * Dialog for adding/editing contacts.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactSchema, type ContactFormData } from '@/lib/validations/job-hunt';
import { useJobHuntStore } from '@/stores/jobHuntStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, X, CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { CompanyContact } from '@/types/job-hunt';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface ContactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact?: CompanyContact;
  onSuccess?: () => void;
}

export function ContactFormDialog({ open, onOpenChange, contact, onSuccess }: ContactFormDialogProps) {
  const { createContact, updateContact } = useJobHuntStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: contact ? {
      company_name: contact.company_name,
      contact_name: contact.contact_name,
      contact_title: contact.contact_title || '',
      contact_email: contact.contact_email || '',
      contact_linkedin: contact.contact_linkedin || '',
      relationship_notes: contact.relationship_notes || '',
      tags: contact.tags,
      next_followup_date: contact.next_followup_date ? new Date(contact.next_followup_date) : undefined,
    } : {
      tags: [],
    },
  });

  const tags = watch('tags') || [];
  const followupDate = watch('next_followup_date');

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setValue('tags', [...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setValue('tags', tags.filter(t => t !== tag));
  };

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        contact_title: data.contact_title || undefined,
        contact_email: data.contact_email || undefined,
        contact_linkedin: data.contact_linkedin || undefined,
        relationship_notes: data.relationship_notes || undefined,
        next_followup_date: data.next_followup_date?.toISOString() || undefined,
      };

      if (contact) {
        await updateContact(contact.id, payload as any);
        toast.success('Contact updated successfully');
      } else {
        await createContact(payload as any);
        toast.success('Contact added successfully');
      }
      
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save contact');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{contact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Contact Name */}
          <div className="space-y-2">
            <Label>Contact Name *</Label>
            <Input {...register('contact_name')} placeholder="John Doe" />
            {errors.contact_name && (
              <p className="text-sm text-destructive">{errors.contact_name.message}</p>
            )}
          </div>

          {/* Company Name */}
          <div className="space-y-2">
            <Label>Company Name *</Label>
            <Input {...register('company_name')} placeholder="Tech Corp" />
            {errors.company_name && (
              <p className="text-sm text-destructive">{errors.company_name.message}</p>
            )}
          </div>

          {/* Contact Title */}
          <div className="space-y-2">
            <Label>Job Title</Label>
            <Input {...register('contact_title')} placeholder="HR Manager" />
            {errors.contact_title && (
              <p className="text-sm text-destructive">{errors.contact_title.message}</p>
            )}
          </div>

          {/* Email & LinkedIn */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input {...register('contact_email')} type="email" placeholder="john@company.com" />
              {errors.contact_email && (
                <p className="text-sm text-destructive">{errors.contact_email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>LinkedIn</Label>
              <Input {...register('contact_linkedin')} type="url" placeholder="https://linkedin.com/in/..." />
              {errors.contact_linkedin && (
                <p className="text-sm text-destructive">{errors.contact_linkedin.message}</p>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="e.g., Recruiter, Hiring Manager"
              />
              <Button type="button" onClick={addTag} variant="outline" size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          {/* Next Follow-up Date */}
          <div className="space-y-2">
            <Label>Next Follow-up Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !followupDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {followupDate ? format(followupDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={followupDate || undefined}
                  onSelect={(date) => setValue('next_followup_date', date || null)}
                  initialFocus
                  className={cn('p-3 pointer-events-auto')}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Relationship Notes */}
          <div className="space-y-2">
            <Label>Relationship Notes</Label>
            <Textarea
              {...register('relationship_notes')}
              placeholder="How you know this person, context of conversations..."
              rows={4}
            />
            {errors.relationship_notes && (
              <p className="text-sm text-destructive">{errors.relationship_notes.message}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {contact ? 'Update Contact' : 'Add Contact'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
